import { storeToRefs } from 'pinia';
import { computed, defineComponent, onMounted } from 'vue';
// eslint-disable-next-line max-len
import GeoLimitCurrencyConfiguration from '@/layouts/ps-management-page/geo-payment-methods-settings/geo-limit-currency-configuration/geo-limit-currency-configuration.vue';
import { useCurrencyListStore } from '@/store/dictionary/currency-list.store';
import UploadImageComponent from '@/components/upload-image-component/upload-image-component.vue';
import SegmentsAutocompleteMultiselect
  from '@/components/segments-autocomplete-multiselect/segments-autocomplete-multiselect.vue';
import AutocompleteSelect from '@/components/autocomplete-select/autocomplete-select.vue';
import { FormInstance } from 'element-plus';
import { useUploadImageStore } from '@/store/upload-image.store';
import {
  geoPaymentMethodsSettingsFormRef,
  useGeoPsManagementStore,
} from '@/store/ps-management/geo-payment-methods.store';
import { geoPaymentMethodsFormRules } from '@/hooks/ps-management/geo-payment-methods-validation-rules';
import { useTagListStore } from '@/store/dictionary/tag-list.store';
import { useEnvStore } from '@/store/env.store';
import { showErrorNotification } from '@/utils/show-notification.util';

export default defineComponent({
  name: 'GeoPaymentMethodsSettings',
  components: {
    GeoLimitCurrencyConfiguration,
    UploadImageComponent,
    SegmentsAutocompleteMultiselect,
    AutocompleteSelect,
  },
  emits: ['updatePaymentMethodList'],
  setup(_props, { emit }) {
    const { WL_SLUG } = useEnvStore();

    const geoPsManagementStore = useGeoPsManagementStore();
    const {
      updateGeoPaymentMethod,
      closeGeoPaymentMethodSettings,
      resetEarlyDeposit,
    } = geoPsManagementStore;
    const {
      geoPaymentMethodsSettingsFormData,
      geoPaymentMethod,
      isGeoPaymentMethodSettingsSidebarVisible,
      isGeoPaymentMethodLoading,
      isWithdrawColumnsDataVisible,
      isDepositColumnsDataVisible,
    } = storeToRefs(geoPsManagementStore);

    const currencyStore = useCurrencyListStore();
    const { isCurrencyListLoading } = storeToRefs(currencyStore);
    const { getCurrencyList } = currencyStore;

    const uploadImageStore = useUploadImageStore();
    const { uploadFile } = uploadImageStore;
    const { selectedImage } = storeToRefs(uploadImageStore);

    const isGeoPaymentMethodsFormDataLoading = computed(() =>
      isCurrencyListLoading.value || isGeoPaymentMethodLoading.value,
    );

    const isCPFCheckerAvailable = computed<boolean>(() =>
      typeof geoPaymentMethod.value?.isCPFCheckerActive === 'boolean',
    );

    const tagListStore = useTagListStore();
    const {
      autocompleteTagListSearchResult,
      isTagListLoading,
      geoPsMethodTagListWithoutIncluded,
      geoPsMethodTagListWithoutExcluded,
    } = storeToRefs(tagListStore);
    const {
      clearTagList,
      tagListAutocompleteSearch,
      clearAutocompleteTagListSearchResult,
    } = tagListStore;

    onMounted(() => {
      getCurrencyList({ isFetchFullList: true, wlSlug: WL_SLUG });
    });

    const saveGeoPaymentMethods = (geoForm?: FormInstance | null): void => {
      if (!geoForm) return;

      geoForm.validate((valid) => {
        if (!valid) {
          return;
        }

        if (selectedImage.value) {
          uploadFile().then((uploadedFilePath) => {
            if (!uploadedFilePath) {
              return;
            }

            geoPaymentMethodsSettingsFormData.value.imageId = uploadedFilePath.uploadedImageFileId;

            updateGeoPaymentMethod(geoPaymentMethodsSettingsFormData.value)
              .then(() => emit('updatePaymentMethodList'))
              .catch(error => {
                showErrorNotification(error);
              });
          });
        } else {
          updateGeoPaymentMethod(geoPaymentMethodsSettingsFormData.value)
            .then(() => emit('updatePaymentMethodList'))
            .catch(error => {
              showErrorNotification(error);
            });
        }
      });
    };

    const getSearchTagList = (tagListPropertyName: 'tagList' | 'excludeTagList') => {
      return (query: string): void => {
        tagListAutocompleteSearch({
          name: query,
          alreadySelectedTagList: geoPaymentMethodsSettingsFormData.value[tagListPropertyName],
        });
      };
    };

    return {
      geoPaymentMethodsSettingsFormData,
      geoPaymentMethodsSettingsFormRef,
      isGeoPaymentMethodsFormDataLoading,
      isCPFCheckerAvailable,
      saveGeoPaymentMethods,
      closeGeoPaymentMethodSettings,
      isGeoPaymentMethodSettingsSidebarVisible,
      isWithdrawColumnsDataVisible,
      isDepositColumnsDataVisible,
      resetEarlyDeposit,
      geoPaymentMethodsFormRules,
      autocompleteTagListSearchResult,
      isTagListLoading,
      clearTagList,
      tagListAutocompleteSearch,
      clearAutocompleteTagListSearchResult,
      getSearchTagList,
      geoPsMethodTagListWithoutIncluded,
      geoPsMethodTagListWithoutExcluded,
    };
  },
});
